import supabase from '../supabaseClient';
import { getEffectiveTeacher } from './substituteUtils';

/**
 * Determines if a user can perform CRUD operations on specific data
 * Substitute teachers can only CRUD their own entries but have full read access like regular teachers
 */

/**
 * Checks if a teacher can create/edit/delete attendance for a specific class and date
 * @param {string} userId - Current user's ID
 * @param {string} classId - Class ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} userRole - User's role (admin, teacher, etc.)
 * @returns {Promise<Object>} Permission object with canCreate, canEdit, canDelete, reason
 */
export const getAttendancePermissions = async (userId, classId, date, userRole) => {
  try {
    // Admins have full permissions
    if (userRole === 'admin' || userRole === 'super_admin') {
      return {
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        reason: 'Admin access'
      };
    }

    // Use the database function to check teacher access
    const { data: accessCheck, error } = await supabase
      .rpc('check_teacher_class_access', {
        teacher_id_param: userId,
        class_id_param: classId,
        check_date: date
      });

    if (error) throw error;

    const access = accessCheck && accessCheck.length > 0 ? accessCheck[0] : null;

    if (!access || !access.has_access) {
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: false,
        reason: 'Not assigned to this class'
      };
    }

    if (access.access_type === 'regular') {
      // Regular teacher: full permissions for their class
      return {
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        reason: 'Regular teacher for this class',
        isSubstitute: false
      };
    } else if (access.access_type === 'substitute') {
      // Substitute teacher: can CRUD their own entries
      return {
        canCreate: true,
        canEdit: true, // Will be further validated to ensure they only edit their own entries
        canDelete: true, // Will be further validated to ensure they only delete their own entries
        canView: true,
        reason: `Substitute teacher for this class and date: ${access.assignment_reason}`,
        isSubstitute: true,
        assignmentId: access.assignment_id,
        substituteReason: access.assignment_reason
      };
    }

    // Default to no permissions
    return {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canView: false,
      reason: 'Unknown access type'
    };
  } catch (error) {
    console.error('Error checking attendance permissions:', error);
    return {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canView: false,
      reason: 'Error checking permissions'
    };
  }
};

/**
 * Checks if a teacher can create/edit/delete Quran progress for a specific class and date
 * @param {string} userId - Current user's ID
 * @param {string} classId - Class ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} userRole - User's role
 * @returns {Promise<Object>} Permission object
 */
export const getQuranProgressPermissions = async (userId, classId, date, userRole) => {
  // Same logic as attendance permissions
  return await getAttendancePermissions(userId, classId, date, userRole);
};

/**
 * Checks if a teacher can create/edit/delete Greenbook progress for a specific class and date
 * @param {string} userId - Current user's ID
 * @param {string} classId - Class ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} userRole - User's role
 * @returns {Promise<Object>} Permission object
 */
export const getGreenbookProgressPermissions = async (userId, classId, date, userRole) => {
  // Same logic as attendance permissions
  return await getAttendancePermissions(userId, classId, date, userRole);
};

/**
 * Validates if a user can edit/delete a specific record based on who created it
 * This ensures substitute teachers can only modify their own entries
 * @param {string} userId - Current user's ID
 * @param {string} recordCreatedBy - ID of who created the record
 * @param {Object} permissions - Permissions from getAttendancePermissions etc.
 * @returns {Object} Updated permissions
 */
export const validateRecordOwnership = (userId, recordCreatedBy, permissions) => {
  // Admins can always edit/delete
  if (permissions.reason === 'Admin access') {
    return permissions;
  }

  // If it's a substitute teacher, they can only edit/delete their own records
  if (permissions.isSubstitute) {
    const canModify = recordCreatedBy === userId;
    return {
      ...permissions,
      canEdit: permissions.canEdit && canModify,
      canDelete: permissions.canDelete && canModify,
      reason: canModify 
        ? permissions.reason 
        : 'Substitute teachers can only modify their own entries'
    };
  }

  // Regular teachers can edit/delete any records for their class
  return permissions;
};

/**
 * Gets class access permissions for a teacher
 * @param {string} userId - Current user's ID
 * @param {string} classId - Class ID
 * @param {string} userRole - User's role
 * @returns {Promise<Object>} Class access permissions
 */
export const getClassAccessPermissions = async (userId, classId, userRole) => {
  try {
    // Admins have full access to all classes
    if (userRole === 'admin' || userRole === 'super_admin') {
      return {
        canAccess: true,
        canManage: true,
        reason: 'Admin access'
      };
    }

    // Check if user is the regular teacher for this class
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('teacher_id, name')
      .eq('id', classId)
      .single();

    if (classError) throw classError;

    if (classData.teacher_id === userId) {
      return {
        canAccess: true,
        canManage: true,
        reason: 'Regular teacher for this class'
      };
    }

    // Check if user is currently a substitute for this class
    const today = new Date().toISOString().split('T')[0];
    const { data: substituteAssignment, error: subError } = await supabase
      .from('substitute_assignments')
      .select('id, start_date, end_date')
      .eq('class_id', classId)
      .eq('substitute_teacher_id', userId)
      .lte('start_date', today)
      .gte('end_date', today)
      .in('status', ['pending'])
      .maybeSingle();

    if (subError) throw subError;

    if (substituteAssignment) {
      return {
        canAccess: true,
        canManage: true, // Substitutes can manage the class during their assignment period
        reason: 'Current substitute teacher for this class',
        isSubstitute: true,
        assignmentId: substituteAssignment.id
      };
    }

    // Check if user has any future substitute assignments for this class
    const { data: futureAssignment, error: futureError } = await supabase
      .from('substitute_assignments')
      .select('id, start_date, end_date')
      .eq('class_id', classId)
      .eq('substitute_teacher_id', userId)
      .gt('start_date', today)
      .in('status', ['pending'])
      .maybeSingle();

    if (futureError) throw futureError;

    if (futureAssignment) {
      return {
        canAccess: true,
        canManage: false, // Can view but not manage until assignment starts
        reason: 'Future substitute teacher for this class',
        isSubstitute: true,
        assignmentId: futureAssignment.id
      };
    }

    // No access
    return {
      canAccess: false,
      canManage: false,
      reason: 'Not assigned to this class'
    };
  } catch (error) {
    console.error('Error checking class access permissions:', error);
    return {
      canAccess: false,
      canManage: false,
      reason: 'Error checking permissions'
    };
  }
};

/**
 * Gets substitute assignment permissions for a teacher
 * @param {string} userId - Current user's ID
 * @param {string} userRole - User's role
 * @param {string} assignmentId - Assignment ID (for editing existing assignments)
 * @returns {Promise<Object>} Substitute assignment permissions
 */
export const getSubstituteAssignmentPermissions = async (userId, userRole, assignmentId = null) => {
  try {
    // Admins can manage all substitute assignments
    if (userRole === 'admin' || userRole === 'super_admin') {
      return {
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        reason: 'Admin access'
      };
    }

    // Teachers can view assignments where they are involved
    const canView = true; // Teachers can see assignments that affect them

    // For editing existing assignments, check if user created it or is involved
    if (assignmentId) {
      const { data: assignment, error } = await supabase
        .from('substitute_assignments')
        .select('created_by, original_teacher_id, substitute_teacher_id')
        .eq('id', assignmentId)
        .single();

      if (error) throw error;

      const isInvolved = assignment.original_teacher_id === userId || 
                        assignment.substitute_teacher_id === userId;
      
      return {
        canCreate: false, // Regular teachers can't create assignments
        canEdit: isInvolved, // Can edit if involved (to update notes, etc.)
        canDelete: false, // Only admins can delete
        canView,
        reason: isInvolved ? 'Involved in this assignment' : 'Limited teacher access'
      };
    }

    // Regular teachers have limited permissions
    return {
      canCreate: false, // Only admins can create substitute assignments
      canEdit: false,
      canDelete: false,
      canView,
      reason: 'Limited teacher access'
    };
  } catch (error) {
    console.error('Error checking substitute assignment permissions:', error);
    return {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canView: false,
      reason: 'Error checking permissions'
    };
  }
};

/**
 * Utility function to check if a user has admin privileges
 * @param {string} userRole - User's role
 * @returns {boolean} Whether user is admin
 */
export const isAdmin = (userRole) => {
  return userRole === 'admin' || userRole === 'super_admin';
};

/**
 * Gets display information about who can modify data on a specific date
 * Useful for UI components to show context about permissions
 * @param {string} classId - Class ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} Display info about effective teacher
 */
export const getEffectiveTeacherDisplay = async (classId, date) => {
  try {
    const effectiveTeacher = await getEffectiveTeacher(classId, date);
    
    return {
      teacherName: `${effectiveTeacher.first_name} ${effectiveTeacher.last_name}`,
      isSubstitute: effectiveTeacher.isSubstitute,
      substituteReason: effectiveTeacher.substituteReason,
      displayText: effectiveTeacher.isSubstitute 
        ? `${effectiveTeacher.first_name} ${effectiveTeacher.last_name} (Substitute)`
        : `${effectiveTeacher.first_name} ${effectiveTeacher.last_name}`
    };
  } catch (error) {
    console.error('Error getting effective teacher display:', error);
    return {
      teacherName: 'Unknown',
      isSubstitute: false,
      displayText: 'Unknown Teacher'
    };
  }
};
